import {
  CallRecordingStatus,
  HydratedCallRecord,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { produce } from 'immer';
import { InfiniteData } from 'react-query';
import { download } from '@frontend/api-call-recordings';
import appConfig from '@frontend/env';
import type { RecentCallRecords } from '@frontend/phone-panels';

export const hasCallRecordingMedia = (
  record?: Pick<HydratedCallRecord, 'channelId' | 'locationId' | 'recordingPath' | 'callRecordingStatus'> | null
): boolean =>
  !!(record?.channelId && record?.locationId && record?.recordingPath) &&
  record?.callRecordingStatus === CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_AVAILABLE;

/**
 * Returns the field updates to apply when a call recording has been deleted.
 */
export const getDeletedRecordingUpdates = (): Pick<
  HydratedCallRecord,
  'recordingPath' | 'recordingFilename' | 'downloadCallRecordingToken' | 'callRecordingStatus'
> => ({
  recordingPath: undefined,
  recordingFilename: undefined,
  downloadCallRecordingToken: '',
  callRecordingStatus: CallRecordingStatus.CALL_RECORDING_STATUS_RECORDING_DELETED,
});

/**
 * Applies a recording-deleted update to an infinite-query cache of recent call records.
 * Patches all pages by channelId.
 */
export const applyRecordingDeletedToInfiniteData = (
  oldData: InfiniteData<RecentCallRecords> | undefined,
  channelId: string
): InfiniteData<RecentCallRecords> | undefined => {
  if (!oldData) return oldData;

  return produce(oldData, (draft) => {
    const updates = getDeletedRecordingUpdates();
    for (const page of draft.pages) {
      const item = page.data.find((record) => record.channelId === channelId);
      if (item) {
        Object.assign(item, updates);
      }
    }
  });
};

export type DownloadCallRecordingParams = {
  channelId?: string;
  locationId?: string;
  token?: string;
};

export const downloadCallRecording = async ({
  channelId,
  locationId,
  token,
}: DownloadCallRecordingParams): Promise<void> => {
  if (!channelId || !locationId) {
    return Promise.reject(new Error('Invalid media data'));
  }
  const tokenParam = token ? `&token=${token}` : '';
  const mediaUrl = `${appConfig.BACKEND_API}/portal/v1/phone/callRecordings/${channelId}?location_id=${locationId}${tokenParam}`;
  const blob: any = await download(mediaUrl);
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${url.split('/')[3]}.mp3`);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
};
